/**
 * Ensures a date string has a UTC timezone indicator ('Z') for correct parsing.
 * 
 * Why this is needed:
 * - When saving dates in GenerateQuote.jsx (line 159), we remove the 'Z' from ISO strings:
 *   `d.toISOString().replace('Z', '') + '000'`
 * - The API returns these dates without timezone info (e.g., "2025-01-15T19:00:00.000000")
 * - JavaScript's Date constructor treats strings without timezone as LOCAL time, not UTC
 * - By appending 'Z', we tell JavaScript to parse it as UTC, which is correct since
 *   the database stores UTC values (they were converted from local to UTC when saved)
 * 
 * @param {string} dateStr - Date string from API (may or may not have timezone indicator)
 * @returns {string} Date string with 'Z' appended if it didn't have a timezone indicator
 */
export const ensureUtcTimezone = (dateStr) => {
  if (!dateStr) return dateStr;
  // If already has timezone indicator, return as-is
  if (dateStr.endsWith('Z') || dateStr.includes('+')) {
    return dateStr;
  }
  // Append 'Z' to treat as UTC
  return dateStr + 'Z';
};

export const formatDateKey = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Converts a date to UTC start of day (00:00:00.000) and returns as ISO string.
 * 
 * @param {Date|string} date - Date to convert
 * @returns {string|null} ISO string of date at start of day, or null if date is invalid
 */
export const toUTCStartDateTime = (date) => {
  if (!date) return null;
  const d = new Date(date);
  if (isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
};

/**
 * Converts a date to UTC end of day (23:59:59.999) and returns as ISO string.
 * 
 * @param {Date|string} date - Date to convert
 * @returns {string|null} ISO string of date at end of day, or null if date is invalid
 */
export const toUTCEndDateTime = (date) => {
  if (!date) return null;
  const d = new Date(date);
  if (isNaN(d.getTime())) return null;
  d.setHours(23, 59, 59, 999);
  return d.toISOString();
};

export const groupFlightsByDate = (flights) => {
  const grouped = {};

  flights.forEach(flight => {
    const dateKey = flight.departureDateKey || formatDateKey(flight.departureDate);
    if (!grouped[dateKey]) {
      grouped[dateKey] = [];
    }
    grouped[dateKey].push(flight);
  });

  Object.keys(grouped).forEach(dateKey => {
    grouped[dateKey].sort((a, b) => a.departureDate - b.departureDate);
  });

  return grouped;
};

export const isWithin48Hours = (date) => {
  const now = new Date();
  const flightDate = new Date(date);
  const diffInHours = (flightDate - now) / (1000 * 60 * 60);
  return diffInHours > 0 && diffInHours <= 48;
};

export const calculateSummaryStats = (flights) => {
  const inFlight = flights.filter(f => f.status === 'IN_TRANSIT');
  const within48Hours = flights.filter(f => isWithin48Hours(f.departureDate));
  const lowTrustScore = flights.filter(f => f.trustScore == "Standard");
  const aogIncidents = flights.filter(f => f.isAOG);
  
  return {
    inFlight: inFlight.length,
    within48Hours: within48Hours.length,
    lowTrustScore: lowTrustScore.length,
    aogIncidents: aogIncidents.length
  };
};

export const getFlightsForDate = (flights, date) => {
  if (!date) return flights;

  const dateKey = formatDateKey(date);
  return flights.filter(flight => (flight.departureDateKey || formatDateKey(flight.departureDate)) === dateKey);
};

export const getWeekNumber = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

/**
 * Gets the ISO week year for a given date.
 * ISO week year is the year that the week belongs to (can differ from calendar year).
 * 
 * @param {Date} date - Date to get ISO week year for
 * @returns {number} ISO week year
 */
export const getISOWeekYear = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  return d.getUTCFullYear();
};

/**
 * Gets the week number range for a month, handling year boundaries.
 * Returns week numbers that belong to the month's calendar year.
 * 
 * @param {Date} month - Date representing the month (any day in the month)
 * @returns {Object} Object with firstWeek and lastWeek (numbers or null)
 */
export const getMonthWeekRange = (month) => {
  const firstDayOfMonth = new Date(month.getFullYear(), month.getMonth(), 1);
  const lastDayOfMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0);
  const selectedYear = month.getFullYear();
  
  const firstWeekYear = getISOWeekYear(firstDayOfMonth);
  const lastWeekYear = getISOWeekYear(lastDayOfMonth);
  
  if (firstWeekYear !== lastWeekYear) {
    let firstWeekInYear = null;
    let lastWeekInYear = null;
    
    for (let day = 1; day <= lastDayOfMonth.getDate(); day++) {
      const date = new Date(month.getFullYear(), month.getMonth(), day);
      const weekYear = getISOWeekYear(date);
      if (weekYear === selectedYear) {
        const weekNum = getWeekNumber(date);
        if (firstWeekInYear === null) {
          firstWeekInYear = weekNum;
        }
        lastWeekInYear = weekNum;
      }
    }
    
    return {
      firstWeek: firstWeekInYear,
      lastWeek: lastWeekInYear,
    };
  }
  
  const firstWeek = getWeekNumber(firstDayOfMonth);
  const lastWeek = getWeekNumber(lastDayOfMonth);
  
  return {
    firstWeek,
    lastWeek,
  };
};

/**
 * Formats week number range into display string.
 * 
 * @param {number|null} firstWeek - First week number
 * @param {number|null} lastWeek - Last week number
 * @returns {string} Formatted week string (e.g., "Week 1", "Week 1-4", or empty string)
 */
export const formatWeekRange = (firstWeek, lastWeek) => {
  if (firstWeek === null || lastWeek === null) {
    return '';
  }
  
  if (firstWeek === lastWeek) {
    return `Week ${firstWeek}`;
  }
  
  return `Week ${firstWeek}-${lastWeek}`;
};

export const getDatesWithFlights = (flights, month) => {
  const dates = new Set();
  const monthYear = month.getFullYear();
  const monthNum = month.getMonth();

  flights.forEach(flight => {
    if (flight.departureDateKey) {
      const [y, m, d] = flight.departureDateKey.split('-').map(Number);
      if (y === monthYear && (m - 1) === monthNum) {
        dates.add(d);
      }
    } else {
      const flightDate = new Date(flight.departureDate);
      if (flightDate.getFullYear() === monthYear && flightDate.getMonth() === monthNum) {
        dates.add(flightDate.getDate());
      }
    }
  });

  return dates;
};

export const dateHasFlights = (flights, date) => {
  if (!date) return false;
  const dateKey = formatDateKey(date);
  return flights.some(flight => (flight.departureDateKey || formatDateKey(flight.departureDate)) === dateKey);
};

export const getWeekStart = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const monday = new Date(d);
  monday.setDate(d.getDate() + diff);
  return monday;
};

export const getWeekDays = (weekStart) => {
  const days = [];
  const start = new Date(weekStart);
  
  for (let i = 0; i < 7; i++) {
    const date = new Date(start);
    date.setDate(start.getDate() + i);
    days.push({
      day: date.getDate(),
      date,
      month: "current",
    });
  }
  
  return days;
};

export const getPreviousWeek = (weekStart) => {
  const prevWeek = new Date(weekStart);
  prevWeek.setDate(prevWeek.getDate() - 7);
  return prevWeek;
};

export const getNextWeek = (weekStart) => {
  const nextWeek = new Date(weekStart);
  nextWeek.setDate(nextWeek.getDate() + 7);
  return nextWeek;
};

export const getCurrentMonthDateRange = (now = new Date()) => {
  const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
  startDate.setHours(0, 0, 0, 0);
  const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  endDate.setHours(23, 59, 59, 999);
  return { startDate, endDate };
};

export function transformQuotesToFlights(rawFlights) {
  if (!Array.isArray(rawFlights)) {
    return [];
  }

  const moment = require('moment-timezone');
  const { getAirportTimezone } = require('@/utils/airportTimezone');
  const { getEffectiveAircraft } = require('@/features/tails/utils');
  const flights = [];

  rawFlights.forEach(item => {
    const client = item.quote?.client || item.client;

    const clientFullName = [client?.first_name, client?.last_name]
      .filter(Boolean)
      .join(' ');

    const baseClientName =
      clientFullName ||
      client?.email ||
      client?.company_name ||
      null;

    const tz = getAirportTimezone(item.departure_airport);
    const departureDateKey = item.departure_date
      ? moment.utc(item.departure_date).tz(tz).format('YYYY-MM-DD')
      : null;

    // Merge per-quote aircraft override so the Track flight list/card
    // shows the tail number the broker actually presented to the client
    // (quote.files.aircraft_overrides[aircraft_id]), not the shared base.
    // Also expose `aircraft` on the transformed record so downstream
    // consumers (FlightCard drawer, TrackFlightView-derived components)
    // can read a pre-merged aircraft without repeating the merge.
    const mergedAircraft = getEffectiveAircraft(item.aircraft, item.quote);

    const flight = {
      id: item.flight_id,
      quoteId: item.quote_id || item.quote?.quote_id,
      quoteNumber: item.quote?.quote_number || '',
      clientId: item.quote?.client_id || '',
      clientEmail: item.quote?.client?.email || '',
      clientName: baseClientName || 'Not provided',
      tailNumber: mergedAircraft?.tail_number || 'TBD',
      aircraft: mergedAircraft,
      quote: item.quote,
      route: {
        from: item.departure_airport,
        to: item.arrival_airport,
      },
      departureAirport: item.departure_airport,
      status: item.flight_status,
      departureDate: new Date(item.departure_date),
      departureDateKey,
      trustScore: item.aircraft?.operator?.rating_tiers,
      isAOG: false,
    };

    flights.push(flight);

  });

  return flights;
}
